import cn from "classnames"
import * as Icons from "@nova/component-icons"
import { RadioButton } from "@nova/ds-component-radio-button"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectWeatherEnabled, selectWeatherPanel } from "features/map/control/selectors"
import { setWeatherMode } from "features/map/control/slice"
import { WeatherMode } from "features/map/control/types"

// [DEPRECATED after 2026-10-06] Radar blends ECCC observed with the model forecast, whose source flips HRRR→RRFS at the RRFS
// go-live (mirrors forecastSource() in weather-data/prefetchRadar).
// once RRFS is operational, drop the conditional and hard-code "ECCC / RRFS (REFC)".
const RRFS_OPERATIONAL_MS = Date.UTC(2026, 9, 7, 12)
const radarSource = Date.now() < RRFS_OPERATIONAL_MS ? "ECCC / HRRR (REFC)" : "ECCC / RRFS (REFC)"

export const WEATHER_MODES: {
    value: WeatherMode
    label: string
    source: string
    Icon: Icons.IconComponent
}[] = [
    { value: "wind", label: "Wind", source: "GFS 10 m", Icon: Icons.Wind },
    {
        value: "precipitation",
        label: "Precipitation",
        source: "GFS (APCP, surface)",
        Icon: Icons.Precipitation,
    },
    { value: "radar", label: "Radar", source: radarSource, Icon: Icons.Radar },
    { value: "lightning", label: "Lightning", source: "GFS (CAPE x APCP)", Icon: Icons.Lightning },
]

type Props = {
    closeOnSelection?: boolean
    onClose?: () => void
    onModeSelect?: (mode: WeatherMode) => void
}

export const SelectWeatherMode = ({ closeOnSelection = false, onClose, onModeSelect }: Props) => {
    const dispatch = useAppDispatch()
    const enabled = useAppSelector(selectWeatherEnabled)
    const { mode } = useAppSelector(selectWeatherPanel)
    const activeMode = enabled ? mode : null

    const select = (value: WeatherMode) => {
        dispatch(setWeatherMode(value))
        onModeSelect?.(value)
        if (closeOnSelection) onClose?.()
    }

    return (
        <div className="flex flex-col gap-1">
            {WEATHER_MODES.map(({ value, label, source, Icon }) => {
                const isActive = value === activeMode
                const tint = isActive ? "text-gray-0" : "text-gray-400"
                return (
                    <div
                        key={value}
                        role="button"
                        tabIndex={0}
                        onClick={() => select(value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault()
                                select(value)
                            }
                        }}
                        className="group flex items-start gap-3 rounded-lg px-2 py-2 cursor-pointer hover:bg-white-100"
                    >
                        <span className="flex h-5 shrink-0 items-center">
                            <RadioButton isSelected={isActive} onClick={() => select(value)} />
                        </span>
                        <span className="flex h-5 shrink-0 items-center">
                            <Icons.SmallIcon
                                Icon={Icon}
                                className={cn(tint, "group-hover:text-white")}
                            />
                        </span>
                        <div className="flex flex-col gap-1">
                            <span
                                className={cn(
                                    "text-[14px] font-normal leading-[1.4] group-hover:text-white",
                                    isActive ? "text-white" : "text-gray-100"
                                )}
                            >
                                {label}
                            </span>
                            <span className="whitespace-nowrap font-mono text-[12px] font-normal leading-[1.4] text-gray-300">
                                {source}
                            </span>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
